import { AlertTriangle, CheckCircle2, OctagonAlert } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { FlagSummary } from '../types';
import type { OutputRendererProps } from './types';

/** The three levels a flag can say, in words and colour. */
export const FLAG_LEVELS = {
  ok: { word: 'OK', Icon: CheckCircle2, tone: 'text-emerald-600 dark:text-emerald-400' },
  warning: { word: 'Warning', Icon: AlertTriangle, tone: 'text-amber-600 dark:text-amber-400' },
  problem: { word: 'Problem', Icon: OctagonAlert, tone: 'text-destructive' },
} as const;

export type FlagLevel = keyof typeof FLAG_LEVELS;

export function flagLevel(summary: unknown): FlagLevel | null {
  const level = (summary as FlagSummary | null | undefined)?.level;
  return level === 'ok' || level === 'warning' || level === 'problem' ? level : null;
}

/** A flag: one status line — its level, coloured, and the action's message. */
export function FlagOutputs({ outputs }: OutputRendererProps) {
  return (
    <>
      {outputs.map((o) => {
        const level = flagLevel(o.summary);
        if (!level) return null;
        const { word, Icon, tone } = FLAG_LEVELS[level];
        const message = (o.summary as FlagSummary).message?.trim();
        return (
          <div key={o.key} className="flex items-start gap-2 text-sm" data-flag={level}>
            <Icon className={cn('mt-0.5 h-4 w-4 shrink-0', tone)} aria-hidden />
            <div className="min-w-0">
              <span className={cn('font-medium', tone)}>
                {o.title ? `${o.title}: ` : ''}
                {word}
              </span>
              {message ? <p className="text-muted-foreground">{message}</p> : null}
            </div>
          </div>
        );
      })}
    </>
  );
}
